export type PdfPreviewErrorCode =
  | 'cancelled'
  | 'password'
  | 'invalid'
  | 'render';

export class PdfPreviewError extends Error {
  public readonly cause?: unknown;

  constructor(
    public readonly code: PdfPreviewErrorCode,
    options?: { cause?: unknown },
  ) {
    super(code);
    this.name = 'PdfPreviewError';
    this.cause = options?.cause;
  }
}

export type PdfThumbnail = {
  dataUrl: string;
  width: number;
  height: number;
};

export type PdfPreviewDocument = {
  pageCount: number;
  renderThumbnail: (
    pageNumber: number,
    options?: { maxWidth?: number; signal?: AbortSignal },
  ) => Promise<PdfThumbnail>;
  renderPageImage: (
    pageNumber: number,
    options: { maxLongEdge: number; signal?: AbortSignal },
  ) => Promise<PdfThumbnail>;
  destroy: () => Promise<void>;
};

type PdfJsModule = typeof PdfJs;

const DEFAULT_THUMBNAIL_WIDTH = 180;
// /assets/* is cached as immutable for one year. When upgrading pdfjs-dist,
// add a new version directory instead of replacing the files at this URL.
const PDFJS_WASM_URL = '/assets/vendor/pdfjs-dist/5.4.149/wasm/';

export const createPdfJsLoader = (
  importer: () => Promise<PdfJsModule> = () => import('pdfjs-dist/webpack.mjs'),
) => {
  let modulePromise: Promise<PdfJsModule> | null = null;

  return async (): Promise<PdfJsModule> => {
    if (typeof window === 'undefined') {
      throw new PdfPreviewError('invalid');
    }
    if (!modulePromise) {
      // PDF.js의 webpack entry가 module worker를 번들에 연결한다. 직접
      // workerSrc를 package URL로 지정하면 Next가 ESM worker를 require로
      // 해석하므로 production build에서 실패한다.
      const currentPromise = importer();
      modulePromise = currentPromise;
      void currentPromise.catch(() => {
        if (modulePromise === currentPromise) modulePromise = null;
      });
    }
    return modulePromise;
  };
};

const loadPdfJs = createPdfJsLoader();

const isAbortError = (error: unknown) =>
  error instanceof DOMException && error.name === 'AbortError';

const normalizePdfError = (
  error: unknown,
  fallback: PdfPreviewErrorCode,
): PdfPreviewError => {
  if (error instanceof PdfPreviewError) return error;
  if (isAbortError(error))
    return new PdfPreviewError('cancelled', { cause: error });

  const name =
    typeof error === 'object' && error !== null && 'name' in error
      ? String(error.name)
      : '';
  if (name === 'PasswordException') {
    return new PdfPreviewError('password', { cause: error });
  }
  if (
    name === 'InvalidPDFException' ||
    name === 'MissingPDFException' ||
    name === 'UnexpectedResponseException'
  ) {
    return new PdfPreviewError('invalid', { cause: error });
  }
  return new PdfPreviewError(fallback, { cause: error });
};

const throwIfAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
};

export const openPdfPreview = async (
  // 업로드 전 File 은 메모리에서, 이미 올라간 첨부는 URL 로 연다.
  source: File | { url: string },
  options?: {
    signal?: AbortSignal;
    pdfJsLoader?: () => Promise<PdfJsModule>;
    /** URL 로 열 때 파일 크기·받은 바이트가 이 값을 넘으면 파싱을 중단한다. */
    maxBytes?: number;
  },
): Promise<PdfPreviewDocument> => {
  const { signal, pdfJsLoader = loadPdfJs, maxBytes } = options ?? {};
  throwIfAborted(signal);

  try {
    const [pdfJs, documentSource] = await Promise.all([
      pdfJsLoader(),
      source instanceof File
        ? source
            .arrayBuffer()
            .then(buffer => ({ data: new Uint8Array(buffer) }))
        : // 자동 선행 다운로드를 끄면 서버가 Range 를 지원할 때 그리는 쪽에 필요한 바이트만 받는다.
          // 지원하지 않으면 pdf.js 가 파일 전체를 받는 방식으로 스스로 물러난다.
          { url: source.url, disableAutoFetch: true, disableStream: true },
    ]);
    throwIfAborted(signal);

    const loadingTask = pdfJs.getDocument({
      ...documentSource,
      stopAtErrors: true,
      wasmUrl: PDFJS_WASM_URL,
    });
    const abortLoading = () => void loadingTask.destroy();
    signal?.addEventListener('abort', abortLoading, { once: true });
    if (maxBytes !== undefined) {
      loadingTask.onProgress = ({
        loaded,
        total,
      }: {
        loaded: number;
        total: number;
      }) => {
        if (Math.max(loaded, total) > maxBytes) abortLoading();
      };
    }

    try {
      const document = await loadingTask.promise;
      throwIfAborted(signal);

      let destroyed = false;
      const activeRenderTasks = new Set<{ cancel: () => void }>();

      const renderPage = async (
        pageNumber: number,
        renderOptions: {
          maxWidth?: number;
          maxLongEdge?: number;
          signal?: AbortSignal;
        },
      ): Promise<PdfThumbnail> => {
        const renderSignal = renderOptions.signal;
        throwIfAborted(renderSignal);
        if (
          destroyed ||
          !Number.isInteger(pageNumber) ||
          pageNumber < 1 ||
          pageNumber > document.numPages
        ) {
          throw new PdfPreviewError('render');
        }

        try {
          const page = await document.getPage(pageNumber);
          throwIfAborted(renderSignal);
          const baseViewport = page.getViewport({ scale: 1 });
          const scale =
            renderOptions.maxLongEdge !== undefined
              ? renderOptions.maxLongEdge /
                Math.max(baseViewport.width, baseViewport.height)
              : (renderOptions.maxWidth ?? DEFAULT_THUMBNAIL_WIDTH) /
                baseViewport.width;
          const viewport = page.getViewport({ scale });
          const canvas = window.document.createElement('canvas');
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          const context = canvas.getContext('2d');
          if (!context) throw new PdfPreviewError('render');

          const renderTask = page.render({
            canvas,
            canvasContext: context,
            viewport,
          });
          activeRenderTasks.add(renderTask);
          const cancelRender = () => renderTask.cancel();
          renderSignal?.addEventListener('abort', cancelRender, {
            once: true,
          });
          try {
            await renderTask.promise;
            throwIfAborted(renderSignal);
            return {
              dataUrl: canvas.toDataURL('image/png'),
              width: canvas.width,
              height: canvas.height,
            };
          } finally {
            renderSignal?.removeEventListener('abort', cancelRender);
            activeRenderTasks.delete(renderTask);
            page.cleanup();
            canvas.width = 0;
            canvas.height = 0;
          }
        } catch (error) {
          throw normalizePdfError(error, 'render');
        }
      };

      return {
        pageCount: document.numPages,
        renderThumbnail(pageNumber, renderOptions) {
          return renderPage(pageNumber, {
            maxWidth: renderOptions?.maxWidth,
            signal: renderOptions?.signal,
          });
        },
        renderPageImage(pageNumber, renderOptions) {
          return renderPage(pageNumber, renderOptions);
        },
        async destroy() {
          if (destroyed) return;
          destroyed = true;
          for (const renderTask of activeRenderTasks) renderTask.cancel();
          activeRenderTasks.clear();
          await loadingTask.destroy();
        },
      };
    } finally {
      signal?.removeEventListener('abort', abortLoading);
    }
  } catch (error) {
    throw normalizePdfError(error, 'invalid');
  }
};
import type * as PdfJs from 'pdfjs-dist';
